import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { History, ShieldCheck, ArrowRight } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Input, Select, TextArea, Card } from "../../components/common/Primitives";
import SectorSuggest from "../../components/startup/SectorSuggest";
import SnapshotHistoryDrawer from "../../components/startup/SnapshotHistoryDrawer";
import PitchPolish from "../../components/startup/PitchPolish";
import DebtIntelConsent from "../../components/debt/DebtIntelConsent";
import FieldTip from "../../components/common/FieldTip";
import AutoSaveIndicator from "../../components/common/AutoSaveIndicator";
import useAutoSave from "../../lib/useAutoSave";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";

/**
 * Startup Profile — pure profile info the founder wants to share.
 *
 * NO verification / ID fields live here anymore. CIN, PAN, GSTIN, LLPIN,
 * Aadhaar, MCA snapshot, and all instant-check panels moved to
 * /startup/verification (with Individual + Business tabs).
 */

const stages = ["Idea", "Pre-Seed", "Seed", "Pre-Series A", "Series A", "Series B", "Growth", "Bootstrapped"];
const sectors = ["Fintech", "CleanTech", "HealthTech", "SaaS", "Consumer", "MSME", "Manufacturing", "Enterprise SaaS", "AgriTech", "EdTech", "Logistics"];

const ACCEPTED_LOGO = "image/png,image/jpeg,image/webp,image/svg+xml";
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

function BrandLogoCard({ hasLogo, updatedAt, onChange }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let revoked = null;
    if (!hasLogo) { setPreviewUrl(null); return; }
    api.get("/startup/logo", { responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        revoked = url;
        setPreviewUrl(url);
      })
      .catch(() => setPreviewUrl(null));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [hasLogo, updatedAt]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) { toast.error("Logo too large (max 2MB)."); e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/startup/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated. It will appear on your next Board-Deck PDF.");
      onChange?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await api.delete("/startup/logo");
      toast.success("Logo removed.");
      setPreviewUrl(null);
      onChange?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <div className="overline mb-2">Brand Logo</div>
      <div className="text-xs text-ink-muted mb-4">
        Embedded at the top of every Board-Deck PDF you export. PNG, JPEG, WebP or SVG · max 2MB.
      </div>
      <div className="flex items-start gap-6">
        <div
          data-testid="brand-logo-preview"
          className="w-36 h-36 rounded-md border border-ink-line/40 bg-ink-surface/50 flex items-center justify-center overflow-hidden"
        >
          {previewUrl ? (
            <img src={previewUrl} alt="Brand logo" className="max-w-full max-h-full object-contain" />
          ) : (
            <div className="text-xs text-ink-muted text-center px-3">No logo uploaded</div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input
            ref={inputRef} type="file" accept={ACCEPTED_LOGO}
            onChange={handleFile} className="hidden" data-testid="brand-logo-input"
          />
          <Button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="brand-logo-upload-btn">
            {hasLogo ? "Replace Logo" : "Upload Logo"}
          </Button>
          {hasLogo && (
            <Button type="button" variant="ghost" onClick={handleDelete} disabled={busy} data-testid="brand-logo-delete-btn">
              Remove
            </Button>
          )}
          {updatedAt && (
            <div className="text-[11px] text-ink-muted mt-1">Updated {formatDateTime(updatedAt)}</div>
          )}
        </div>
      </div>
    </Card>
  );
}

function VerificationCta({ form }) {
  const kycOk = form?.kyc_status === "Approved";
  return (
    <Card testId="verification-cta">
      <div className="flex items-start gap-3 flex-wrap">
        <ShieldCheck size={22} className={kycOk ? "text-emerald-600" : "text-gold"} />
        <div className="flex-1 min-w-[220px]">
          <div className="font-heading text-ink text-lg">
            {kycOk ? "Your identity is verified" : "Complete your Verification"}
          </div>
          <div className="text-xs text-ink-muted mt-1">
            {kycOk
              ? "CIN, GSTIN, Business PAN, personal Aadhaar & PAN — all confirmed. Investors see your Trust badge."
              : "Verify Aadhaar (DigiLocker), personal + business PAN, CIN and GSTIN on the Verification page. This is required before your profile is shared with investors."}
          </div>
        </div>
        <Link
          to="/startup/verification"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-navy text-white text-xs font-heading hover:bg-[#256E58] transition-colors"
          data-testid="link-to-verification"
        >
          {kycOk ? "Review" : "Go to Verification"} <ArrowRight size={12} />
        </Link>
      </div>
    </Card>
  );
}

export default function StartupProfile() {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = () => api.get("/startup/me").then((r) => { setForm(r.data.startup || {}); setLoading(false); });
  useEffect(() => { load(); }, []);

  const doSave = async (silent = false) => {
    await api.put("/startup/profile", form);
    if (!silent) toast.success("Profile saved.");
  };
  const { status: saveStatus, lastSavedAt } = useAutoSave(form, () => doSave(true), 20000, !loading);
  const save = async (e) => {
    e.preventDefault();
    try { await doSave(false); } catch (err) { toast.error(formatApiError(err)); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (loading) return <DashboardShell title="Profile"><div className="text-ink-muted">Loading…</div></DashboardShell>;

  return (
    <DashboardShell title="Profile" subtitle="STARTUP · IDENTITY"
      actions={
        <div className="flex items-center gap-3">
          <button
            onClick={() => setHistoryOpen(true)}
            className="text-xs px-3 py-1.5 rounded-md border border-navy/15 text-ink hover:bg-navy/[0.04] inline-flex items-center gap-1.5"
            data-testid="open-snapshot-history-profile"
          >
            <History size={12} /> History
          </button>
          <AutoSaveIndicator status={saveStatus} lastSavedAt={lastSavedAt} />
        </div>
      }
    >
      <Toaster richColors position="top-right" />
      <SnapshotHistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} kind="profile" onReverted={() => load()} />

      <div className="grid gap-6 max-w-4xl">
        <VerificationCta form={form} />
        <BrandLogoCard hasLogo={!!form.logo_storage_path} updatedAt={form.logo_updated_at} onChange={load} />
        <DebtIntelConsent />

        {/* Individual / founder section */}
        <Card>
          <div className="overline mb-3">Individual · Founder</div>
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="startup-profile-founder-form">
            <Input label="Founder Name" value={form.founder_name || ""} onChange={set("founder_name")} testId="sp-founder" />
            <Input label="Designation" value={form.founder_designation || ""} onChange={set("founder_designation")} testId="sp-founder-designation" placeholder="Founder & CEO" />
            <Input label="Founder Mobile" value={form.founder_mobile || ""} onChange={set("founder_mobile")} testId="sp-founder-mob" />
            <Input label="LinkedIn URL" value={form.founder_linkedin || ""} onChange={set("founder_linkedin")} testId="sp-founder-linkedin" placeholder="https://linkedin.com/in/…" />
            <Input label="Date of Birth" type="date" value={form.founder_dob || ""} onChange={set("founder_dob")} testId="sp-founder-dob" />
            <Input label="Founder Address" value={form.founder_address || ""} onChange={set("founder_address")} testId="sp-founder-address" placeholder="Street, City, State" />
            <div className="md:col-span-2">
              <Button type="submit" data-testid="sp-founder-save-btn">Save Founder Info</Button>
            </div>
          </form>
        </Card>

        {/* Company section */}
        <Card>
          <div className="overline mb-3">Company</div>
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="startup-profile-company-form">
            <Input label="Company Display Name" value={form.startup_name || ""} onChange={set("startup_name")} testId="sp-startup-name" />
            <Input label="Legal Entity Name" value={form.legal_entity_name || ""} onChange={set("legal_entity_name")} testId="sp-legal-name" />
            <Input label="Website" value={form.website || ""} onChange={set("website")} testId="sp-website" />
            <Input label="City" value={form.city || ""} onChange={set("city")} testId="sp-city" />
            <Input label="State" value={form.state || ""} onChange={set("state")} testId="sp-state" />
            <Input label="Country" value={form.country || "India"} onChange={set("country")} testId="sp-country" />
            <div>
              <Select label="Sector" value={form.sector || ""} onChange={set("sector")} testId="sp-sector">
                <option value="">Select sector</option>
                {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
              <SectorSuggest
                description={form.one_line_pitch}
                onePitch={form.one_line_pitch}
                currentSector={form.sector}
                onPick={(s) => setForm((f) => ({ ...f, sector: s }))}
              />
            </div>
            <Input label="Industry" value={form.industry || ""} onChange={set("industry")} testId="sp-industry" />
            <Select label="Business Stage" value={form.business_stage || ""} onChange={set("business_stage")} testId="sp-stage">
              <option value="">Select stage</option>
              {stages.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
            <Input
              label="Year of Incorporation"
              type="number"
              value={form.year_of_incorporation || ""}
              onChange={(e) => setForm({ ...form, year_of_incorporation: e.target.value ? Number(e.target.value) : undefined })}
              testId="sp-year"
            />
            <Input
              label="Employees"
              type="number"
              value={form.num_employees || ""}
              onChange={(e) => setForm({ ...form, num_employees: e.target.value ? Number(e.target.value) : undefined })}
              testId="sp-emp"
            />
            <div className="md:col-span-2">
              <TextArea label="One-line Pitch" value={form.one_line_pitch || ""} onChange={set("one_line_pitch")} testId="sp-pitch" />
              <div className="flex items-start justify-between gap-3 mt-1">
                <div className="text-[11px] text-ink-muted flex items-center gap-1">
                  <FieldTip
                    why="This is the first line every investor reads. Aim for one crisp sentence."
                    example="'B2B SaaS that automates SME GST filings — used by 40+ CAs.'"
                    impact="A clear pitch improves match-hit-rate by ~2.4×."
                  />
                  <span>Keep it under 120 characters.</span>
                </div>
                <PitchPolish
                  field="one_line_pitch"
                  text={form.one_line_pitch || ""}
                  onAccept={(rewrite) => setForm((f) => ({ ...f, one_line_pitch: rewrite }))}
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <TextArea label="Company Description" value={form.description || ""} onChange={set("description")} testId="sp-description" rows={4} />
            </div>
            <div className="md:col-span-2">
              <Button type="submit" data-testid="sp-save-btn">Save Profile</Button>
            </div>
          </form>
        </Card>

        <div className="text-[11px] text-ink-muted italic">
          Looking for CIN, PAN, GSTIN, or the MCA snapshot? Those live on the
          <Link to="/startup/verification" className="text-emerald-700 hover:underline mx-1 font-heading">Verification page</Link>
          so they don&apos;t clutter your profile.
        </div>
      </div>
    </DashboardShell>
  );
}
